import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandFormDialog } from "@/components/admin/BrandFormDialog";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { asArray, errMsg, formatDate, http } from "@/lib/api";

export function BrandsManager() {
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    http.get("/brands?limit=500").then(({ data }) => setBrands(asArray(data))).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (b) => {
    try {
      await http.delete(`/admin/brands/${b.id}`);
      toast.success(`${b.name} deleted`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-brands">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ph-muted">{brands.length} brands</p>
        <button type="button" onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-add-brand-btn"
          className="inline-flex items-center gap-2 rounded-full bg-ph-lime px-5 py-2.5 text-sm font-semibold text-ph-navy hover:shadow-[0_0_24px_rgba(182,255,92,0.45)]">
          <Plus className="h-4 w-4" /> Add brand
        </button>
      </div>
      <div className="mt-5 overflow-hidden rounded-3xl border border-ph-line bg-ph-surface">
        {brands.map((b) => (
          <div key={b.id} className="flex items-center gap-4 border-b border-ph-line px-5 py-4 last:border-b-0" data-testid={`admin-brand-row-${b.slug}`}>
            <BrandLogo brand={b} className="h-11 w-11 rounded-xl" textClass="text-xs" />
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg font-bold text-ph-ink">{b.name} {b.featured && (
                <span className={`ml-1 rounded-full px-2 py-0.5 align-middle font-mono-ph text-[10px] font-bold uppercase tracking-wider ${b.featured_active ? "bg-ph-lime text-ph-navy" : "bg-ph-raised text-ph-muted"}`} data-testid={`admin-brand-featured-${b.slug}`}>
                  {b.featured_active ? (b.featured_until ? `Featured until ${formatDate(b.featured_until)}` : "Featured") : "Promotion ended"}
                </span>
              )}</p>
              <p className="truncate text-xs text-ph-muted">{(b.categories ?? []).join(" · ") || "No categories"}</p>
            </div>
            <span className="hidden font-mono-ph text-xs text-ph-muted sm:inline">{b.doc_count} lists · {b.download_count} dl</span>
            <button type="button" onClick={() => { setEditing(b); setOpen(true); }} data-testid={`admin-edit-brand-${b.slug}`} aria-label="Edit brand" className="rounded-full p-2 text-ph-muted hover:bg-ph-raised hover:text-ph-ink">
              <Pencil className="h-4 w-4" />
            </button>
            <ConfirmDelete testId={`admin-delete-brand-${b.slug}`} title={`Delete ${b.name}?`} description="The brand and all of its price lists will be removed from the directory." onConfirm={() => remove(b)} />
          </div>
        ))}
      </div>
      <BrandFormDialog open={open} onOpenChange={setOpen} brand={editing} onSaved={load} />
    </div>
  );
}
